<template>
    <div :class="mainClass">
        <page-header v-if="includeHeader" :title="title" :items="breadcrumbs" />

        <tabs
            v-if="tabsElements.length"
            :items="tabsElements"
            :active-item="activeTab"
            :actions="tabActions"
            is-card
            class="mb-3"
            @action="onTabAction"
            @item-change="activeTab = $event"
        />

        <awesome-table
            :key="tableKey"
            :labels="tableLabels"
            :items="items"
            :pagination="pagination"
            :filters="filters"
            :options="options"
            :is-loading="isLoading"
            :get-row-class="getRowClass"
            :deleted-item-id="deletedItemId"
            @row-click="onRowClick"
            @pagination-change="onPaginationChange"
            @add-button-click="onAddButtonClick"
            @filter-change="onFilterChange"
        >
            <template #globalActionButtons>
                <button
                    v-if="importPath"
                    v-b-modal.import-csv
                    class="btn btn-success mr-1 mb-1"
                >
                    <i class="fas fa-upload" />
                    Import
                </button>

                <button
                    v-for="action in globalActions"
                    :key="action.modal"
                    :class="action.class"
                    @click.stop="action.method"
                >
                    <i :class="action.icon" />
                    {{ action.text }}
                </button>
            </template>

            <template #[`labels.actions`]="{ label }">
                <span style="color: gray">
                    {{ label }}
                </span>
            </template>
            <template #[`items.actions`]="{ item, index }">
                <button
                    v-for="(action, actionIndex) in tableActions"
                    v-show="isActionVisible(action, item)"
                    :key="`action_${actionIndex}`"
                    class="action-button"
                    @click.stop="callAction(action, item.id, index)"
                >
                    <i :class="action.icon" />
                </button>
            </template>

            <template #[`items.productVariants`]="{ item }">
                <div v-if="item.productVariants.length">
                    <div
                        v-for="(productVariant, index) in item.productVariants"
                        :key="index"
                    >
                        <span>
                            {{ productVariant.Order2ProductVariant.quantity }} -
                            {{ productVariant.product.name }} -
                            {{
                                getProductAttributes(productVariant.attributes)
                            }}
                        </span>
                    </div>
                </div>
                <div v-else>
                    <span>-</span>
                </div>
            </template>
        </awesome-table>

        <import-csv-modal
            v-if="importPath"
            :path="importPath"
            @imported="fetchItems"
        />
        <confirmation-modal @agree="removeItem" @disagree="idToDelete = null" />

        <component
            :is="item.name"
            v-for="item of additionalComponents"
            :key="item.name"
            v-bind="item.props || {}"
            v-on="item.events || {}"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import TableView from '@/components/elements/views/TableView';

export default {
    page: {
        title: 'Orders'
    },

    extends: TableView,

    data() {
        return {
            pageTitle: 'Orders',
            includeDeleteAction: false
        };
    },

    computed: {
        ...mapGetters({
            STATUSES: 'orders/FILTER_STATUSES'
        }),

        labels() {
            return [
                { value: 'number', type: 'number', width: '130px' },
                {
                    value: 'createdAt',
                    text: 'Date',
                    filter: 'formatDate',
                    width: '150px',
                    type: 'date'
                },
                {
                    value: 'totalPrice',
                    type: 'number',
                    filter: 'formatPrice',
                    width: '150px'
                },
                {
                    value: 'status',
                    type: 'enum',
                    enumOptions: this.STATUSES.map(({ value }) => value),
                    width: '150px'
                },
                {
                    value: 'address',
                    width: '180px'
                },
                {
                    value: 'note',
                    width: '180px'
                },
                {
                    value: 'user.fullName',
                    sortFieldName: 'user.firstName',
                    text: "User's name",
                    width: '180px'
                },
                {
                    value: 'user.email',
                    sortFieldName: 'user.email',
                    text: "User's email",
                    width: '180px'
                },
                {
                    value: 'user.tz',
                    sortFieldName: 'user.tz',
                    text: "User's TZ",
                    width: '180px'
                },
                {
                    width: '200px',
                    text: "User's campuses",
                    sortable: false,
                    value: 'user.campuses',
                    filterFieldName: 'user.campuses.name',
                    filter: 'joinTable'
                },
                {
                    value: 'shop.name',
                    text: 'Shop Name',
                    width: '180px'
                },
                {
                    value: 'productVariants',
                    text: 'Ordered Products',
                    width: '200px',
                    filter: 'productVariants',
                    filterFieldName: 'productVariants.product.name',
                    sortable: false
                },
                {
                    value: 'paymentMethod',
                    type: 'enum',
                    enumOptions: ['credit', 'stars', 'credit&stars', 'manual'],
                    width: '150px'
                },
                {
                    value: 'isExported',
                    type: 'boolean',
                    isBadge: true,
                    width: '100px'
                }
            ];
        }
    },

    methods: {
        ...mapActions({
            getItems: 'orders/index'
        }),

        onRowClick({ id }) {
            this.$router.push(`/orders/${id}/edit`);
        },

        onAddButtonClick() {
            this.$router.push('/orders/add');
        },

        getProductAttributes(attributes) {
            return attributes
                .map(value => `${value.group.name}: ${value.value}`)
                .join(', ');
        }
    }
};
</script>
